/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { ContentEditableEvent, getUrlSync, RichText, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect, OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import {
    EmailRegex,
    getOrgUnitLocationsByArea,
    GetOrgUnitLocationsByAreaInput,
    IFullOrgUnitAvailability
} from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    IModalProps,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import {
    ISyscoBusinessSignUpConfig,
    ISyscoBusinessSignUpProps,
    ISyscoBusinessSignUpResources
} from './sysco-business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpLabel } from './components';
import { IBusinessSignUpRadioButtonOptionProps, BusinessSignUpRadioButtonOption } from './components/sign-up-radio-button';
import {
    ICreateWebFormInstanceTokenResponse,
    ICuisineTypeDetail,
    IRefreshWebFormInstanceTokenResponse,
    IStyleOfBusinessEntity,
    ITaxExemptionCheckResponse,
    ITaxFormPreFilledFieldsEntity
} from '../../custom-actions/DataServiceEntities.g';
import MsDyn365 from '@msdyn365-commerce/core';
import {
    createDefaultFormInstanceTokenAsync,
    createExistingCustomerUserAsync,
    createUniqueProspectAsync,
    createWebFormInstanceTokenAsync,
    refreshWebFormInstanceTokenAsync,
    taxExemptionCheckAsync,
    validateOrganizationAsync
} from '../../custom-actions/DataActionExtension.g';
import { getAllStylesOfBusinessAsync } from '../../custom-actions/DataActionExtension.g';
import DescriptionLabel from './components/description-label';

export interface IBusinessSignUpExtendedProps extends ISyscoBusinessSignUpProps<ISyscoBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface IAllStores {
    storeLocations: ISingleStore[];
}

const errorCircleSvg = (
    <svg xmlns='http://www.w3.org/2000/svg' width='20px' height='20px' viewBox='0 0 21 21'>
        <g fill='none' fill-rule='evenodd' stroke='#dc3232' stroke-linecap='round' stroke-linejoin='round' transform='translate(2 2)'>
            <circle cx='8.5' cy='8.5' r='8' />
            <g transform='matrix(0 1 -1 0 17 0)'>
                <path d='m5.5 11.5 6-6' />

                <path d='m5.5 5.5 6 6' />
            </g>
        </g>
    </svg>
);

export interface ISingleStore {
    OrgUnitName?: string;
    OrgUnitNumber?: string;
    Street?: string;
    StreetNumber?: string;
    City?: string;
    DistrictName?: string;
    BuildingCompliment?: string;
    Postbox?: string;
    Zip?: string;
    County?: string;
    State?: string;
    StateName?: string;
    Country?: string;
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
    description?: React.ReactNode;
}

export interface IBusinessState {
    isReady: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
    existingCustomerValue: string;
    isResale: boolean;
    dbaValue: string;
    styleOfBusinesses: IStyleOfBusinessEntity[];
    cusineTypes: ICuisineTypeDetail[];
    styleofbusiness: string;
    selectedCuisineType: string;
    selectedCuisineId: string;
    taxId: string;
    storeName: string;
    taxStatus: string;
    termsAndConditions: boolean;
    agreedToTaxPolicy: boolean;
    blobFileNames: string;
    docuSignWebFormURL: string;
    docuSignInstanceId: string;
    docuSignInstanceToken: string;
    docuSignWebFormId: string;
    isModalOpen: boolean;
    loadDocuSign: boolean;
    allStores: OrgUnitLocation[];
    uploadedFileNamesBlob: { plainNames: string[]; withTimeStamp: string[] };
    filesToUpload: File[];
    sasToken: string;
    blobStorageURL: string;
    uploading: boolean;
    uploadProgress: { [key: string]: number };
    SignUpVia: string;
    emailMarketing: boolean;
    phoneMarketing: boolean;
    integrationKey: string;
    hasFormErros: boolean;
    termsAndCondtionsModal: boolean;
    taxPolicyModal: boolean;
    phonePolicyModal: boolean;
    isLoading: boolean;
    isTaxEempted: boolean;
    moduleUpdating: boolean;
    isExitSystemModal: boolean;
    isStyleOfBusinessChangeModal: boolean;
    existingUserSyscoValidationFalseModal: boolean;
    existingUserSyscoValidationTrueModal: boolean;
    isExistingUserSyscoValidationResultLoading: boolean;
    existingUserSyscoValidationResult: null | {
        EmailAlreadyExists: boolean;
        IsAccountFound: boolean;
        OrganizationName?: undefined | string;
        ValidationResult: boolean;
        AccountValidation: boolean;
    };
    isExistingUserSyscoreroutingLoading: boolean;
    inputDirty: { [key: string]: boolean };
    isProspectAvailable: boolean;
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
    existingUSBLCustomer: React.ReactNode;
    renderReSaleDropDown: React.ReactNode;
    renderSubmitTaxDropDown: React.ReactNode;
    renderSalesTaxWarning: React.ReactNode;
    renderStyleOfBusiness: React.ReactNode;
    renderCusineType: React.ReactNode;
    renderTaxNode: React.ReactNode;
    renderStore: React.ReactNode;
    renderTermsAndConditions: React.ReactNode;
    renderTaxPolicy: React.ReactNode;
    renderModal: React.ReactNode;
    emailPolicy: React.ReactNode;
    phonePolicy: React.ReactNode;
    renderExitModal: React.ReactNode;
    renderStyleChangeModal: React.ReactNode;
    existingValidationErrorModal: React.ReactNode;
    existingValidationSuccessModal: React.ReactNode;
}

export interface IFormItem {
    id: string;
    type: InputType;
    label: string;
    value: string;
    maxChars?: string;
    infoBox?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    fieldReference?: React.RefObject<HTMLInputElement>;
    radioButtonOptions?: IBusinessSignUpRadioButtonOptionProps[];
    radioButtonGroupClass?: string;
    description?: string;
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea',
    RadioButton = 'radiobutton'
}

export enum InputID {
    FirstName = 'FirstName',
    LastName = 'LastName',
    Email = 'CompanyEmail',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    JobTitle = 'JobTitle',
    Size = 'CompanySize',
    Department = 'Department',
    Comment = 'Comment',
    ProspectType = 'ProspectType',
    AccountNo = 'AccountNumber',
    StreetAddress = 'StreetAddress',
    ZipCode = 'ZipCode',
    NetworkId = 'SyscoNetworkID',
    Phone = 'Phone'
}

export const renderInputSection = (
    isProspectAvailable: boolean,
    isUsbl: boolean,
    config: ISyscoBusinessSignUpConfig,
    resources: ISyscoBusinessSignUpResources,
    inputDirty: { [key: string]: boolean },
    inputs: IFormItem[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    showErrors: boolean,
    addressForm?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address) => void
): IBusinessSignUpItem[] => {
    return inputs.map((element, index) => {
        const {
            id,
            type,
            label,
            maxChars,
            description,
            isRequired,
            pattern,
            customClass,
            value,
            fieldReference,
            radioButtonOptions,
            radioButtonGroupClass
        } = element;
        let css = id;
        if (id === InputID.Email && isUsbl) {
            css = 'usblEmail';
        }

        const baseSignUpItem: IBusinessSignUpItem = {
            wrapper: {
                className: classnames(className, `${className}-${css}`, customClass)
            },
            id,
            description: description ? <DescriptionLabel {...{ id, className, text: description }} /> : <></>,
            label: <SignUpLabel {...{ id, forId: id, className, text: label }} />,
            errorMessage: <SignUpError {...{ className, type: 'item' }} />
        };

        if (type === InputType.Address) {
            if (addressForm) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(addressForm as React.ReactElement, {
                            updateForm: updateAddress,
                            hasError: showErrors,
                            isRequired: isRequired === undefined ? false : isRequired,
                            resetAddress: needToReset
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else if (type === InputType.RadioButton) {
            baseSignUpItem.input = (
                <div className={radioButtonGroupClass}>
                    {radioButtonOptions &&
                        radioButtonOptions.map((element, index) => {
                            return <BusinessSignUpRadioButtonOption key={index} {...element}></BusinessSignUpRadioButtonOption>;
                        })}
                </div>
            );
        } else {
            const checkIfError = (): React.ReactNode => {
                if (!element?.isRequired) {
                    return null;
                }
                const { id, value, pattern } = element;
                const {
                    emailValidationError,
                    genericValidationError,
                    fieldRequiredError,
                    streetValidationError,
                    zipValidationError
                } = resources;
                if (!value) {
                    return (
                        <>
                            {errorCircleSvg} <span>{fieldRequiredError}</span>
                        </>
                    );
                }
                const isInvalidPattern = pattern && !new RegExp(pattern).test(value);
                if (isInvalidPattern) {
                    const errorMessages: Record<string, string> = {
                        [InputID.Email]: emailValidationError,
                        [InputID.StreetAddress]: streetValidationError,
                        [InputID.ZipCode]: zipValidationError
                    };

                    return (
                        <>
                            {errorCircleSvg} <span>{errorMessages[id] || genericValidationError}</span>
                        </>
                    );
                }
                return null;
            };
            const { prospectAlreadyExistsError } = resources;
            const { configProspectAlreadyExistsError } = config;

            baseSignUpItem.input = (
                <>
                    <SignUpInput
                        {...{
                            id,
                            type,
                            value,
                            maxLength: maxChars,
                            pattern,
                            className,
                            isRequired: isRequired === undefined ? false : isRequired,
                            onChange: onInputChange,
                            fieldReference
                        }}
                    />
                    {inputDirty && inputDirty?.[id] && <div className='input-error'>{checkIfError()}</div>}
                    {isProspectAvailable && id === InputID.Email && (
                        <div className='input-error'>
                            {errorCircleSvg} <span>{configProspectAlreadyExistsError ?? prospectAlreadyExistsError}</span>
                        </div>
                    )}
                    {element?.infoBox && <div className={'ms-business-sign-up__form-item__warning'}>{element?.infoBox}</div>}
                </>
            );
        }

        return baseSignUpItem;
    });
};

/**
 *
 * Business Request Form Modules
 * Form that a business can use to submit a request to become a partner with Microsoft.
 * @extends {React.Component<ISyscoBusinessSignUpProps<ISyscoBusinessSignUpConfig>>}
 */
class BusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    private readonly moduleClassName: string = 'ms-business-sign-up';
    private readonly prospectRadioButtonName: string = 'business-sign-up-radio-button';

    // Used as default data
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: InputID.AccountNo,
            type: InputType.Text,
            label: this.props.resources.syscoAccountNoLabel,
            value: '',
            isRequired: true,
            maxChars: '6',
            description: this.props.resources.accountNoDescription,
            customClass: 'width-100'
        },
        {
            id: InputID.FirstName,
            type: InputType.Text,
            label: this.props.resources.firstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            label: this.props.resources.lastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label: this.props.resources.legalCompanyNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.Address,
            type: InputType.Address,
            label: this.props.resources.companyAddressLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100'
        },
        {
            id: InputID.StreetAddress,
            type: InputType.Text,
            label: this.props.resources.streetNameLabel,
            value: '',
            maxChars: '5',
            pattern: '^[0-9]{1,}$',
            isRequired: true,
            customClass: 'width-50',
            infoBox: this.props.config?.streetInfoBox ?? this.props.resources.streetInfoBox
        },
        {
            id: InputID.ZipCode,
            type: InputType.Text,
            label: this.props.resources.zipCodeLabel,
            value: '',
            isRequired: true,
            maxChars: '10',
            pattern: '^\\d{5}(-\\d{4})?$',
            customClass: 'width-50'
        },
        {
            id: InputID.Email,
            type: InputType.Email,
            label: this.props.resources.businessEmailAddressLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50'
        },
        {
            id: InputID.NetworkId,
            type: InputType.Text,
            label: this.props.resources.syscoNetworkIdLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.Phone,
            type: InputType.Text,
            label: this.props.resources.mobilePhoneLabel,
            value: '',
            pattern: '^(?:\\d{10}|\\d{3}-\\d{3}-\\d{4}|\\(\\d{3}\\)\\d{3}-\\d{4}|\\(\\d{3}\\) \\d{3}-\\d{4})$',
            isRequired: true,
            customClass: 'width-50'
        }
    ];

    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;
    private docusignContainerRef: React.RefObject<HTMLDivElement>;
    private styleOfBusinessRef: React.RefObject<HTMLSelectElement>;
    private cusineTypeRef: React.RefObject<HTMLSelectElement>;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);

        this.state = {
            isReady: false,
            formItems: this.props.formItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false,
            existingCustomerValue: 'no',
            isResale: true,
            dbaValue: '',
            styleOfBusinesses: [],
            cusineTypes: [],
            styleofbusiness: '',
            selectedCuisineType: '',
            selectedCuisineId: '',
            taxId: '',
            storeName: '',
            taxStatus: 'Not submitted',
            agreedToTaxPolicy: false,
            termsAndConditions: false,
            docuSignWebFormURL: '',
            docuSignWebFormId: '',
            docuSignInstanceId: '',
            docuSignInstanceToken: '',
            blobFileNames: '',
            isModalOpen: false,
            loadDocuSign: false,
            allStores: [],
            uploadedFileNamesBlob: { withTimeStamp: [], plainNames: [] },
            filesToUpload: [],
            sasToken: '',
            blobStorageURL: '',
            uploading: false,
            uploadProgress: {},
            SignUpVia: '',
            emailMarketing: false,
            phoneMarketing: false,
            integrationKey: '',
            hasFormErros: true,
            termsAndCondtionsModal: false,
            taxPolicyModal: false,
            phonePolicyModal: false,
            isLoading: false,
            isTaxEempted: false,
            moduleUpdating: false,
            isExitSystemModal: false,
            isStyleOfBusinessChangeModal: false,
            existingUserSyscoValidationFalseModal: false,
            existingUserSyscoValidationTrueModal: false,
            isExistingUserSyscoValidationResultLoading: false,
            existingUserSyscoValidationResult: null,
            isExistingUserSyscoreroutingLoading: false,
            inputDirty: {},
            isProspectAvailable: false
        };
        this.docusignContainerRef = React.createRef();
        this.styleOfBusinessRef = React.createRef();
        this.cusineTypeRef = React.createRef();
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public async componentDidMount(): Promise<void> {
        const getAllStyles = await getAllStylesOfBusinessAsync({ callerContext: this.props.context.actionContext });
        if (getAllStyles) {
            // debugger;
            this.setState({ styleOfBusinesses: getAllStyles });
            // let x = getAllStyles.find(item => item.StyleOfBusinessName === "Caterer" || )
        }
        const getAllStores = await getOrgUnitLocationsByArea(
            new GetOrgUnitLocationsByAreaInput(0, 0, 0, 0, true),
            this.props.context.actionContext
        );
        if (getAllStores && getAllStores.length && getAllStores.length > 0) {
            const allStores: OrgUnitLocation[] = [];
            getAllStores.map((item: IFullOrgUnitAvailability, index: number) => {
                if (item && item.OrgUnitAvailability && item.OrgUnitAvailability.OrgUnitLocation) {
                    if (!allStores.includes(item.OrgUnitAvailability.OrgUnitLocation)) {
                        allStores.push(item.OrgUnitAvailability.OrgUnitLocation);
                    }
                }
            });
            this.setState({ allStores: allStores });
        }
        this.formChildReference.current?.focus();
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { formItems } = this.state;
        const { className, heading } = config;
        // formItems.push(this._renderRadioButtons())
        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            existingUSBLCustomer: this._renderRadioButtonExistingUSBLCustomer(),
            heading: (
                <Text
                    className={`${this.moduleClassName}__heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            businessSignUpForm: this._renderForm(),
            formItems,
            renderReSaleDropDown: this.state.existingCustomerValue === 'yes' ? <></> : this._renderResaleDropDown(),
            renderSubmitTaxDropDown: this.state.existingCustomerValue === 'yes' ? <></> : this._renderSubmitTaxDropDown(),
            renderSalesTaxWarning: this.state.isResale ? null : this._renderSalesTaxWarning(),
            renderStyleOfBusiness: this.state.existingCustomerValue === 'yes' ? <></> : this._renderStyleOfBusiness(),
            renderCusineType: this.state.existingCustomerValue === 'yes' ? <></> : this._renderCusineTypes(),
            renderTaxNode: this.state.existingCustomerValue === 'yes' ? <></> : this._renderTaxInput(),
            renderStore: this.state.existingCustomerValue === 'yes' ? <></> : this._renderStore(),
            renderTermsAndConditions: this._renderTermsAndConditions(),
            renderTaxPolicy: this._renderTaxPolicy(),
            renderModal: this._renderModal(),
            emailPolicy: this._renderMarketingPolicyEmail(),
            phonePolicy: this._renderMarketingPolicyPhone(),
            renderExitModal: this._onExitSystem(),
            existingValidationErrorModal: this._onValidationError(),
            existingValidationSuccessModal: this._onValidationSuccess(),
            renderStyleChangeModal: this._onChangeStyleOfBusiness()
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _checkFormValidation = () => {
        this.setState({ isReady: this._isFormReady() });
    };

    private _onExitSystem = (): React.ReactNode => {
        const {
            resources: { exitSystemMessage }
        } = this.props;
        const modalPropsTerms: IModalProps = {
            applicationNode: MsDyn365.isBrowser ? document.body : '',
            isOpen: this.state.isExitSystemModal,
            autoFocus: true,
            size: 'sm',
            keyboard: true,
            backdrop: true,
            zIndex: 1000,
            fade: true,
            className: `${this.state.loadDocuSign ? 'document-sign' : ''}`
        };
        return (
            <Modal {...modalPropsTerms}>
                {!this.state.loadDocuSign && (
                    <ModalHeader
                        closeAriaLabel='Close'
                        closeButtonLabel='Close'
                        toggle={() => this.setState({ isExitSystemModal: !this.state.isExitSystemModal, isModalOpen: true })}
                    ></ModalHeader>
                )}
                <ModalBody>{exitSystemMessage}</ModalBody>
                <ModalFooter style={{ justifyContent: 'flex-end' }}>
                    <SignUpButton
                        key='exit-ok'
                        id='exit-ok'
                        className={`${this.moduleClassName}__create-button`}
                        ariaLabel={'exit-ok'}
                        text={'Ok'}
                        disabled={false}
                        onClick={() => this._submitProspectCall()}
                        telemetryContent={this.telemetryContent}
                    />
                    <SignUpButton
                        key='exit-cancel'
                        id='exit-cancel'
                        className={`${this.moduleClassName}__create-button`}
                        ariaLabel={'exit-cancel'}
                        text={'Cancel'}
                        disabled={false}
                        onClick={() => this.setState({ isExitSystemModal: !this.state.isExitSystemModal, isModalOpen: true })}
                        telemetryContent={this.telemetryContent}
                    />
                </ModalFooter>
            </Modal>
        );
    };

    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };
    private _handleChangeForTermsAndConditions = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked === true) {
            this.setState(
                {
                    termsAndConditions: event.target.checked,
                    hasSubmitErrors: false,
                    termsAndCondtionsModal: false,
                    hasFormErros: this._isFormHasErros()
                },
                () => {
                    this._checkFormValidation();
                }
            );
        } else {
            this.setState({ termsAndConditions: event.target.checked, hasSubmitErrors: true, hasFormErros: this._isFormHasErros() }, () => {
                this._checkFormValidation();
            });
        }
    };
    private _renderTermsAndConditions = (): React.ReactNode => {
        const {
            resources: { linkText, termsAndCondtions, termAndConditionsHeading },
            config: { termsAndConditionsRichText }
        } = this.props;
        const modalProps: IModalProps = {
            applicationNode: MsDyn365.isBrowser ? document.body : '',
            isOpen: this.state.termsAndCondtionsModal,
            autoFocus: true,
            size: 'sm',
            keyboard: true,
            backdrop: true,
            zIndex: 1000,
            fade: true,
            toggle: () =>
                this.setState(prevState => ({
                    termsAndCondtionsModal: !prevState.termsAndCondtionsModal // Toggle the boolean value
                }))
        };
        return (
            <div className='ms-business-sign-up__form-item ms-business-sign-up__form-item-terms width-100'>
                <label className='ms-checkout-terms-and-conditions__container'>
                    <input
                        type='checkbox'
                        checked={this.state.termsAndConditions}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            // if (event.target.checked === true) {
                            this.setState(prevState => ({
                                termsAndCondtionsModal: !prevState.termsAndCondtionsModal // Toggle the boolean value
                            }));
                            // }
                        }}
                        className='ms-checkout-terms-and-conditions__input-checkbox'
                    />
                    <span className='ms-checkout-terms-and-conditions__text'>
                        &nbsp;{termsAndCondtions}&nbsp;
                        <a
                            href='javascript:void(0)' // Prevents navigation
                            onClick={() =>
                                this.setState(prevState => ({
                                    termsAndCondtionsModal: !prevState.termsAndCondtionsModal // Toggle the boolean value
                                }))
                            }
                            className='ms-checkout__terms-and-conditions__link'
                            style={{ color: '#008cd2' }}
                        >
                            {linkText}
                        </a>
                    </span>
                </label>
                <Modal {...modalProps}>
                    <ModalHeader
                        closeAriaLabel='Close'
                        closeButtonLabel='Close'
                        toggle={() =>
                            this.setState(prevState => ({
                                termsAndCondtionsModal: !prevState.termsAndCondtionsModal // Toggle the boolean value
                            }))
                        }
                    >
                        {termAndConditionsHeading}
                    </ModalHeader>
                    <ModalBody>
                        <RichTextComponent text={termsAndConditionsRichText} />
                        <div style={{ marginTop: '30px' }}>
                            <label className='ms-checkout-terms-and-conditions__container'>
                                <input
                                    style={{ marginLeft: 0 }}
                                    type='checkbox'
                                    checked={this.state.termsAndConditions}
                                    onChange={this._handleChangeForTermsAndConditions}
                                    className='ms-checkout-terms-and-conditions__input-checkbox'
                                />
                                &nbsp;
                                <span className='ms-checkout-terms-and-conditions__text'>{termsAndCondtions}</span>
                            </label>
                        </div>
                    </ModalBody>
                </Modal>
            </div>
        );
    };

    private _isFormHasErros = (): boolean => {
        const {
            styleofbusiness,
            termsAndConditions,
            agreedToTaxPolicy,
            existingCustomerValue,
            storeName,
            cusineTypes,
            selectedCuisineType
        } = this.state;
        const isCuisineTypeRequired = existingCustomerValue !== 'yes' && cusineTypes.length > 0;
        if (existingCustomerValue === 'yes') {
            if (!termsAndConditions || !agreedToTaxPolicy) {
                return true;
            } else {
                return false;
            }
        } else {
            if (
                styleofbusiness === '' ||
                !termsAndConditions ||
                !agreedToTaxPolicy ||
                !storeName ||
                (isCuisineTypeRequired && !selectedCuisineType)
            ) {
                return true;
            } else {
                return false;
            }
        }
    };
    private _handleChangeForTaxPolicy = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked === true) {
            this.setState(
                {
                    agreedToTaxPolicy: event.target.checked,
                    hasSubmitErrors: false,
                    taxPolicyModal: false,
                    hasFormErros: this._isFormHasErros()
                },
                () => {
                    this._checkFormValidation();
                }
            );
        } else {
            this.setState({ agreedToTaxPolicy: event.target.checked, hasSubmitErrors: true, hasFormErros: this._isFormHasErros() }, () => {
                this._checkFormValidation();
            });
        }
    };
    private _renderTaxPolicy = (): React.ReactNode => {
        const {
            resources: { linkText, customerPolicy, taxPolicyHeading },
            config: { taxPolicyRichText }
        } = this.props;
        const modalPropsTerms: IModalProps = {
            applicationNode: MsDyn365.isBrowser ? document.body : '',
            isOpen: this.state.taxPolicyModal,
            autoFocus: true,
            size: 'sm',
            keyboard: true,
            backdrop: true,
            zIndex: 1000,
            fade: true,
            toggle: () =>
                this.setState(prevState => ({
                    taxPolicyModal: !prevState.taxPolicyModal // Toggle the boolean value
                }))
        };
        return (
            <div className='ms-business-sign-up__form-item ms-business-sign-up__form-item-terms width-100' style={{ marginTop: '5px' }}>
                <label className='ms-checkout-terms-and-conditions__container'>
                    <input
                        type='checkbox'
                        checked={this.state.agreedToTaxPolicy}
                        onChange={this._handleChangeForTaxPolicy}
                        className='ms-checkout-terms-and-conditions__input-checkbox'
                    />
                    <span className='ms-checkout-terms-and-conditions__text'>
                        &nbsp;{customerPolicy}&nbsp;
                        <a
                            href='javascript:void(0)'
                            onClick={() =>
                                this.setState(prevState => ({
                                    taxPolicyModal: !prevState.taxPolicyModal // Toggle the boolean value
                                }))
                            }
                            className='ms-checkout__terms-and-conditions__link'
                            style={{ color: '#008cd2' }}
                        >
                            {linkText}
                        </a>
                    </span>
                </label>
                <Modal {...modalPropsTerms}>
                    <ModalHeader
                        closeAriaLabel='Close'
                        closeButtonLabel='Close'
                        toggle={() =>
                            this.setState(prevState => ({
                                taxPolicyModal: !prevState.taxPolicyModal // Toggle the boolean value
                            }))
                        }
                    >
                        {taxPolicyHeading}
                    </ModalHeader>
                    <ModalBody>
                        <RichTextComponent text={taxPolicyRichText} />
                        {/* <label className='ms-checkout-terms-and-conditions__container'>
                            <input
                                type='checkbox'
                                checked={this.state.agreedToTaxPolicy}
                                onChange={this._handleChangeForTaxPolicy}
                                className='ms-checkout-terms-and-conditions__input-checkbox'
                            />
                            <span className='ms-checkout-terms-and-conditions__text'>{customerPolicy}</span>
                        </label> */}
                    </ModalBody>
                </Modal>
            </div>
        );
    };

    private _renderMarketingPolicyEmail = (): React.ReactNode => {
        const {
            resources: { emailMarketingText }
        } = this.props;
        return (
            <div className='ms-business-sign-up__form-item ms-business-sign-up__form-item-terms width-100' style={{ marginTop: '5px' }}>
                <label className='ms-checkout-terms-and-conditions__container'>
                    <input
                        type='checkbox'
                        checked={this.state.emailMarketing}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            this.setState({ emailMarketing: event.target.checked });
                        }}
                        className='ms-checkout-terms-and-conditions__input-checkbox'
                    />
                    &nbsp;<span className='ms-checkout-terms-and-conditions__text'>{emailMarketingText}</span>
                </label>
            </div>
        );
    };

    private _handleMarketingPolicyPhone = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked === true) {
            this.setState({ phoneMarketing: event.target.checked, hasSubmitErrors: false });
        } else {
            this.setState({ phoneMarketing: event.target.checked, hasSubmitErrors: true });
        }
    };
    private _renderMarketingPolicyPhone = (): React.ReactNode => {
        const {
            resources: { phoneMarketingText, linkText, phonePolicyHeading },
            config: { textPolicyRichText }
        } = this.props;

        const modalPropsTerms: IModalProps = {
            applicationNode: MsDyn365.isBrowser ? document.body : '',
            isOpen: this.state.phonePolicyModal,
            autoFocus: true,
            size: 'sm',
            keyboard: true,
            backdrop: true,
            zIndex: 1000,
            fade: true,
            toggle: () =>
                this.setState(prevState => ({
                    phonePolicyModal: !prevState.phonePolicyModal // Toggle the boolean value
                }))
        };
        return (
            <div className='ms-business-sign-up__form-item ms-business-sign-up__form-item-terms width-100' style={{ marginTop: '5px' }}>
                <label className='ms-checkout-terms-and-conditions__container'>
                    <input
                        type='checkbox'
                        checked={this.state.phoneMarketing}
                        onChange={this._handleMarketingPolicyPhone}
                        className='ms-checkout-terms-and-conditions__input-checkbox'
                    />
                    &nbsp;
                    <span className='ms-checkout-terms-and-conditions__text'>
                        {phoneMarketingText} &nbsp;
                        <a
                            href='javascript:void(0)'
                            onClick={() =>
                                this.setState(prevState => ({
                                    phonePolicyModal: !prevState.phonePolicyModal // Toggle the boolean value
                                }))
                            }
                            className='ms-checkout__terms-and-conditions__link'
                            style={{ color: '#008cd2' }}
                        >
                            {linkText}
                        </a>
                    </span>
                </label>
                <Modal {...modalPropsTerms}>
                    <ModalHeader
                        closeAriaLabel='Close'
                        closeButtonLabel='Close'
                        toggle={() =>
                            this.setState(prevState => ({
                                phonePolicyModal: !prevState.phonePolicyModal // Toggle the boolean value
                            }))
                        }
                    >
                        {phonePolicyHeading}
                    </ModalHeader>
                    <ModalBody>
                        <RichTextComponent text={textPolicyRichText} />
                    </ModalBody>
                </Modal>
            </div>
        );
    };

    private handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === '') {
            this.setState({ existingCustomerValue: event.target.value, hasSubmitErrors: true });
        } else {
            this.setState({ existingCustomerValue: event.target.value, hasSubmitErrors: false, isReady: this._isFormReady() }, () => {
                this._checkFormValidation();
            });
        }
    };

    private _renderRadioButtonExistingUSBLCustomer = (): React.ReactNode => {
        const {
            resources: { existingSyscoCustomer, existingSyscoCustomerNote }
        } = this.props;
        return (
            <div className='customer-form'>
                <label className='ms-business-sign-up__form-item-label'>{existingSyscoCustomer}</label>
                <label
                    className='ms-business-sign-up__form-item-label'
                    style={{ fontSize: '14px', color: 'gray', display: 'block', marginBottom: '10px' }}
                >
                    {existingSyscoCustomerNote}
                </label>
                <div className='radio-group'>
                    <label>
                        <input
                            type='radio'
                            name='existingUSBLCustomer'
                            value='yes'
                            checked={this.state.existingCustomerValue === 'yes'}
                            onChange={this.handleChange}
                        />
                        Yes (Sí)
                    </label>
                    <label>
                        <input
                            type='radio'
                            name='existingUSBLCustomer'
                            value='no'
                            checked={this.state.existingCustomerValue === 'no'}
                            onChange={this.handleChange}
                        />
                        No (No)
                    </label>
                </div>
            </div>
        );
    };

    private handleChangeForResale = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (event.target.value === 'yes') {
            this.setState(
                {
                    isResale: true
                },
                () => {
                    this.setState({ hasFormErros: this._isFormHasErros() });
                }
            );
        } else {
            this.setState(
                {
                    isResale: false
                },
                () => {
                    this.setState({ hasFormErros: this._isFormHasErros() });
                }
            );
        }
    };

    private _renderResaleDropDown = (): React.ReactNode => {
        const styles = {
            dropdownFocus: {
                borderColor: '#007bff' // Highlight border on focus
            }
        };
        const {
            resources: { reSaleOrTaxExempt }
        } = this.props;
        return (
            <div className='ms-business-sign-up__form-item ms-business-sign-up__form-item-resale width-50'>
                <label className='ms-business-sign-up__form-item-label'>{reSaleOrTaxExempt}</label>
                <select
                    className='msc-address-form__dropdown'
                    value={this.state.isResale ? 'yes' : 'no'}
                    onChange={this.handleChangeForResale}
                    onFocus={e => (e.target.style.borderColor = styles.dropdownFocus.borderColor)}
                    onBlur={e => (e.target.style.borderColor = '#ccc')}
                >
                    <option value='yes'>Yes (Sí)</option>
                    <option value='no'>No (No)</option>
                </select>
            </div>
        );
    };

    private handleChangeSubmitTax = (event: any) => {
        if (event.target.value === '') {
            this.setState({ dbaValue: event.target.value, hasSubmitErrors: true });
        } else {
            this.setState({ dbaValue: event.target.value, hasSubmitErrors: false });
        }
    };
    private _renderSubmitTaxDropDown = (): React.ReactNode => {
        return (
            <div className='ms-business-sign-up__form-item ms-business-sign-up__form-item-submit-tax-document width-50'>
                <label className='ms-business-sign-up__form-item-label'>{this.props.resources.dba}</label>
                <input
                    className='msc-address-form__input msc-address-form__input-text'
                    type='text'
                    onKeyUp={this.handleChangeSubmitTax}
                    placeholder=''
                />
            </div>
        );
    };

    private _renderSalesTaxWarning = (): React.ReactNode => {
        const {
            resources: { salesTaxWarningDetail, headingForWarning }
        } = this.props;
        return (
            <div className='ms-business-sign-up__form-item__warning'>
                <p>
                    <b>{headingForWarning}</b> {salesTaxWarningDetail}
                </p>
            </div>
        );
    };

    private handleChangeStyleOfBusiness = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedStyleOfBusiness = this.state.styleOfBusinesses.find(style => style.RECID.toString() === event.target.value);
        const selectedCuisineTypes = selectedStyleOfBusiness?.CuisineTypes ?? [];

        this.setState(
            {
                styleofbusiness: event.target.value,
                cusineTypes: selectedCuisineTypes,
                selectedCuisineType: '',
                selectedCuisineId: '',
                hasSubmitErrors: event.target.value === '',
                hasFormErros: this._isFormHasErros()
            },
            () => {
                this._checkFormValidation();
            }
        );
    };

    private handleChangeCuisineType = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const item = this.state.cusineTypes.find(item => item.CuisineType === event.target.value);
        this.setState(
            {
                selectedCuisineType: event.target.value,
                selectedCuisineId: item?.CuisineTypeRecId ? item?.CuisineTypeRecId.toString() : '',
                hasSubmitErrors: event.target.value === '',
                hasFormErros: this._isFormHasErros()
            },
            () => {
                this._checkFormValidation();
            }
        );
    };
    private _renderStyleOfBusiness = (): React.ReactNode => {
        const {
            resources: { styleOfBusiness }
        } = this.props;
        return (
            <div className='ms-business-sign-up__form-item ms-business-sign-up__form-item-business-style width-50'>
                <label className='ms-business-sign-up__form-item-label'>{styleOfBusiness}</label>
                <select
                    ref={this.styleOfBusinessRef}
                    className='msc-address-form__dropdown'
                    value={this.state.styleofbusiness}
                    onChange={this.handleChangeStyleOfBusiness}
                >
                    <option value='' disabled>
                        Select
                    </option>
                    {this.state.styleOfBusinesses &&
                        this.state.styleOfBusinesses.length &&
                        this.state.styleOfBusinesses.length > 0 &&
                        this.state.styleOfBusinesses.map((style: IStyleOfBusinessEntity) => {
                            return (
                                <option value={style.RECID} key={style.RECID}>
                                    {style.StyleOfBusinessName}
                                </option>
                            );
                        })}
                </select>
            </div>
        );
    };

    private _renderCusineTypes = (): React.ReactNode => {
        const {
            resources: { cusineTypes }
        } = this.props;
        return (
            <>
                {this.state.cusineTypes && this.state.cusineTypes.length && this.state.cusineTypes.length > 0 ? (
                    <div className='ms-business-sign-up__form-item ms-business-sign-up__form-item-CompanyEmail width-50'>
                        <label className='ms-business-sign-up__form-item-label'>{cusineTypes}</label>
                        <select
                            ref={this.cusineTypeRef}
                            className='msc-address-form__dropdown'
                            value={this.state.selectedCuisineType}
                            onChange={this.handleChangeCuisineType}
                            disabled={this.state.cusineTypes.length === 0}
                        >
                            <option value=''>Select</option>
                            {this.state.cusineTypes &&
                                this.state.cusineTypes.length &&
                                this.state.cusineTypes.length > 0 &&
                                this.state.cusineTypes.map((style: ICuisineTypeDetail) => {
                                    return (
                                        <option value={style.CuisineType} key={style.CuisineType}>
                                            {style.SubType === '' || style.SubType === '—' ? style.CuisineType : style.SubType}
                                        </option>
                                    );
                                })}
                        </select>
                    </div>
                ) : null}
            </>
        );
    };

    private handleTaxIdChange = (event: any) => {
        if (event.target.value === '' && this.state.isResale) {
            this.setState({ taxId: event.target.value, hasSubmitErrors: true });
        } else {
            this.setState({ taxId: event.target.value, hasSubmitErrors: false });
        }
    };
    private _renderTaxInput = (): React.ReactNode => {
        return (
            <div className='ms-business-sign-up__form-item ms-business-sign-up__form-item-tax-id width-100'>
                <label className='ms-business-sign-up__form-item-label'>{this.props.resources.taxId}</label>
                <input
                    className='msc-address-form__input msc-address-form__input-text'
                    type='text'
                    onKeyUp={this.handleTaxIdChange}
                    placeholder=''
                />
            </div>
        );
    };

    private handleChangeForStore = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (event.target.value === '') {
            this.setState({ storeName: event.target.value, hasSubmitErrors: true, hasFormErros: this._isFormHasErros() }, () => {
                this._checkFormValidation();
            });
        } else {
            this.setState({ storeName: event.target.value, hasSubmitErrors: false, hasFormErros: this._isFormHasErros() }, () => {
                this._checkFormValidation();
            });
        }
    };
    private _renderStore = (): React.ReactNode => {
        return (
            <div className='ms-business-sign-up__form-item ms-business-sign-up__form-item-store width-50'>
                <label className='ms-business-sign-up__form-item-label'>{this.props.resources.preferredStoreLabel}</label>
                <select className='msc-address-form__dropdown' value={this.state.storeName} onChange={this.handleChangeForStore}>
                    <option value='' disabled>
                        Select
                    </option>
                    {this.state.allStores &&
                        this.state.allStores.length &&
                        this.state.allStores.length > 0 &&
                        this.state.allStores.map((singleStore: OrgUnitLocation) => {
                            return (
                                <option value={singleStore.OrgUnitNumber} key={singleStore.OrgUnitNumber}>
                                    {singleStore.OrgUnitName}
                                </option>
                            );
                        })}
                </select>
            </div>
        );
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots } = this.props;
        const { showAddressErrors, hasSubmitErrors, isReady, formItems } = this.state;
        let updatedArray = formItems;
        if (this.state.existingCustomerValue === 'yes') {
            updatedArray = formItems.filter(
                item =>
                    item.id === InputID.AccountNo ||
                    item.id === InputID.FirstName ||
                    item.id === InputID.LastName ||
                    item.id === InputID.Email ||
                    item.id === InputID.StreetAddress ||
                    item.id === InputID.ZipCode ||
                    item.id === InputID.Phone
            );
        } else {
            const selectedStyleOfBusiness = this.state.styleOfBusinesses?.find(
                style => style.RECID.toString() === this.state.styleofbusiness
            );
            if (selectedStyleOfBusiness?.StyleOfBusinessName === 'Sysco Colleague') {
                updatedArray = formItems.filter(
                    item =>
                        item.id !== InputID.AccountNo &&
                        item.id !== InputID.StreetAddress &&
                        item.id !== InputID.ZipCode &&
                        item.id !== InputID.Phone
                );
            } else {
                updatedArray = formItems.filter(
                    item =>
                        item.id !== InputID.AccountNo &&
                        item.id !== InputID.StreetAddress &&
                        item.id !== InputID.ZipCode &&
                        item.id !== InputID.NetworkId &&
                        item.id !== InputID.Phone
                );
            }
        }
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            partialAddressErrorMessage
        } = resources;
        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;
        const form = {
            FormWrapper: {
                className: `${this.moduleClassName}__form `
            },
            items: renderInputSection(
                this.state.isProspectAvailable,
                this.state.existingCustomerValue === 'yes',
                config,
                resources,
                this.state.inputDirty,
                updatedArray,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange.bind(this),
                showAddressErrors,
                addressForm,
                this._onAddressChange
            ),
            buttons: [
                <SignUpButton
                    key='continue'
                    id='continue'
                    isLoading={isReady && (!!this.state.isExistingUserSyscoValidationResultLoading || !!this.state.isLoading)}
                    className={`${this.moduleClassName}__create-button`}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    disabled={!isReady}
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    isLoading={false}
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                )
            ],
            disclaimer: config.disclaimer && (
                <RichTextComponent className={`${this.moduleClassName}__disclaimer`} text={config.disclaimer} />
            )
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private _onInputChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        if (event.currentTarget.id === InputID.Email && this.state.isProspectAvailable) {
            this.setState({ isProspectAvailable: false });
        }
        const newForm = this.state.formItems;
        newForm.forEach(item => {
            if (item.type === InputType.RadioButton && event.target.name === this.prospectRadioButtonName) {
                item.value = event.target.value;
            } else if (item.id === event.target.id) {
                this.setState(prevState => ({
                    inputDirty: { ...prevState.inputDirty, [item.id]: true }
                }));
                item.value = event.target.value;
            }
        });

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        // ? (this._isFormHasErros() ? false : true) : false
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    }

    private _addedReference(): IFormItem[] {
        const newFormFields = this.BusinessSignUpDefaultInputs;
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (errors: boolean, currentAddress?: Address): void => {
        // Find address item
        const newForm = this.state.formItems;
        const addressItem = newForm.find(item => {
            return item.type === InputType.Address;
        });

        // If found --> set to currentAddress
        if (addressItem) {
            addressItem.address = currentAddress;

            // Update state with new values and set errors to false if address is not required field
            //  ? (this._isFormHasErros() ? false : true) : false
            this.setState(
                {
                    formItems: newForm,
                    addressHasErrors: errors,
                    showAddressErrors: false
                },
                () => {
                    this._checkFormValidation();
                }
            );
        }
    };

    private readonly _isFormReady = (): boolean => {
        const { formItems } = this.state;
        let updatedArray = formItems;
        if (this.state.existingCustomerValue === 'yes') {
            updatedArray = formItems.filter(
                item =>
                    item.id === InputID.AccountNo ||
                    item.id === InputID.FirstName ||
                    item.id === InputID.LastName ||
                    item.id === InputID.Email ||
                    item.id === InputID.StreetAddress ||
                    item.id === InputID.ZipCode ||
                    item.id === InputID.Phone
            );
        } else {
            const selectedStyleOfBusiness = this.state.styleOfBusinesses?.find(
                style => style.RECID.toString() === this.state.styleofbusiness
            );
            if (selectedStyleOfBusiness?.StyleOfBusinessName === 'Sysco Colleague') {
                updatedArray = formItems.filter(
                    item =>
                        item.id !== InputID.AccountNo &&
                        item.id !== InputID.StreetAddress &&
                        item.id !== InputID.ZipCode &&
                        item.id !== InputID.Phone
                );
            } else {
                updatedArray = formItems.filter(
                    item =>
                        item.id !== InputID.AccountNo &&
                        item.id !== InputID.StreetAddress &&
                        item.id !== InputID.ZipCode &&
                        item.id !== InputID.NetworkId &&
                        item.id !== InputID.Phone
                );
            }
        }

        for (const currentInput of updatedArray) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern

            if (currentInput.isRequired) {
                if (currentInput.type === InputType.Address) {
                    if (this.state.addressHasErrors) {
                        return false;
                    }
                } else if (currentInput.value === undefined || currentInput.value === '') {
                    return false;
                }
            }
            if (currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }

        if (this._isFormHasErros()) {
            return false;
        }
        return true;
    };

    private _submitProspectCall = async () => {
        const { config, context } = this.props;
        this.setState({ isLoading: true });

        // Check for matching id and if not empty
        const firstName = this._getItem(InputID.FirstName);
        const lastName = this._getItem(InputID.LastName);
        const companyName = this._getItem(InputID.CompanyName);
        const companyEmail = this._getItem(InputID.Email);
        const companyAddress = this._getItem(InputID.Address, true);

        // API gets email as part of the address object, but I do not use the email input from the address component, thus we need to copy it over
        if (companyEmail && companyEmail.value && companyAddress && companyAddress.address) {
            companyAddress.address.Email = companyEmail.value;
        }
        let newTaxStatus = '';
        if (this.state.SignUpVia === 'azureblob' || this.state.SignUpVia === 'docusign') {
            newTaxStatus = 'Pending Approval';
        } else if (this.state.SignUpVia === 'docuSignInComplete') {
            newTaxStatus = 'In-progress';
        } else {
            newTaxStatus = 'Not submitted';
        }

        const selectedStyleOfBusiness = this.state.styleOfBusinesses.find(style => style.RECID.toString() === this.state.styleofbusiness);

        let newPartner: BusinessPartnerProspect = {
            FirstName: firstName && firstName.value,
            LastName: lastName && lastName.value,
            CompanyName: companyName && companyName.value,
            Address: companyAddress && companyAddress.address && companyAddress.address,
            ProspectTypeValue: 1,
            ExtensionProperties: [
                {
                    Key: 'StyleOfBusiness',
                    Value: {
                        StringValue: selectedStyleOfBusiness?.StyleOfBusinessName
                    }
                },
                {
                    Key: 'Store',
                    Value: {
                        StringValue: this.state.storeName
                    }
                },
                {
                    Key: 'TaxExemptorResale',
                    Value: {
                        BooleanValue: this.state.isResale
                    }
                },
                {
                    Key: 'TaxId',
                    Value: {
                        StringValue: this.state.taxId
                    }
                },
                {
                    Key: 'BlobFilesNames',
                    Value: {
                        StringValue:
                            this.state.uploadedFileNamesBlob &&
                            this.state.uploadedFileNamesBlob.withTimeStamp &&
                            this.state.uploadedFileNamesBlob.withTimeStamp.length &&
                            this.state.uploadedFileNamesBlob.withTimeStamp.length > 0
                                ? JSON.stringify(this.state.uploadedFileNamesBlob.withTimeStamp)
                                : '' // Put blob files names here // Put blob files names here
                    }
                },
                {
                    Key: 'DocuSignWebformId',
                    Value: {
                        StringValue: this.state.SignUpVia !== 'azureblob' ? this.state.docuSignWebFormId : '' // Put DocuSignWebFormId
                    }
                },
                {
                    Key: 'DocuSignInstanceId',
                    Value: {
                        StringValue: this.state.SignUpVia !== 'azureblob' || this.state.SignUpVia ? this.state.docuSignInstanceId : '' // Put DocuSignInstanceId
                    }
                },
                {
                    Key: 'TaxStatus',
                    Value: {
                        StringValue: newTaxStatus
                    }
                },
                {
                    Key: 'DBA',
                    Value: {
                        StringValue: this.state.dbaValue
                    }
                },
                {
                    Key: 'MarketingOptInMail',
                    Value: {
                        BooleanValue: this.state.emailMarketing
                    }
                },
                {
                    Key: 'MarketingOptInPhone',
                    Value: {
                        BooleanValue: this.state.phoneMarketing
                    }
                }
            ]
        };
        if (newPartner?.Address?.Phone) {
            newPartner.Address.Phone = newPartner?.Address?.Phone.replace(/[-() ]/g, '');
        }
        if (selectedStyleOfBusiness?.StyleOfBusinessName === 'Sysco Colleague' && newPartner.ExtensionProperties) {
            const networkId = this._getItem(InputID.NetworkId);
            newPartner.ExtensionProperties.push({
                Key: 'NetworkID',
                Value: {
                    StringValue: networkId?.value
                }
            });
        }

        if (
            (selectedStyleOfBusiness?.StyleOfBusinessName === 'Caterer' || selectedStyleOfBusiness?.StyleOfBusinessName === 'Restaurant') &&
            newPartner.ExtensionProperties
        ) {
            newPartner.ExtensionProperties.push({
                Key: 'OrgCuisineType',
                Value: {
                    StringValue: this.state.selectedCuisineType
                }
            });
            newPartner.ExtensionProperties.push({
                Key: 'OrgCuisineTypeRecId',
                Value: {
                    StringValue: this.state.selectedCuisineId
                }
            });
        }

        this.setState({
            isReady: false
        });
        // Create business partner request
        const res = await createUniqueProspectAsync({ callerContext: context.actionContext }, newPartner);
        if (!res.ProspectAlreadyExists) {
            const successPageURL =
                (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
                getUrlSync('home', this.props.context.actionContext) ||
                '';
            window.location.href = successPageURL;
            this.setState({ isLoading: false });
        } else {
            this.setState(
                {
                    hasSubmitErrors: true,
                    isLoading: false,
                    isProspectAvailable: true,
                    isExitSystemModal: false
                },
                () => {
                    setTimeout(() => {
                        const errorElement = document.getElementById('CompanyEmail_label');
                        if (errorElement) {
                            const yOffset = -150;
                            const y = errorElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
                            window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                    }, 1000);
                }
            );
        }
    };

    // private focusStyleOfBusiness = () => {
    //     if (this.styleOfBusinessRef.current) {
    //         this.styleOfBusinessRef.current.focus({ preventScroll: true });
    //         this.styleOfBusinessRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    //     }
    // };

    private _onChangeStyleOfBusiness = (): React.ReactNode => {
        const {
            config: { styleOfBusinessTaxExemptionHeading, styleOfBusinessTaxExemptionMessage }
        } = this.props;
        const modalPropsTerms: IModalProps = {
            applicationNode: MsDyn365.isBrowser ? document.body : '',
            isOpen: this.state.isStyleOfBusinessChangeModal,
            autoFocus: true,
            size: 'sm',
            keyboard: true,
            backdrop: true,
            zIndex: 1000,
            fade: true
        };
        return (
            <Modal {...modalPropsTerms}>
                {!this.state.loadDocuSign && (
                    <ModalHeader
                        closeAriaLabel='Close'
                        closeButtonLabel='Close'
                        toggle={() => this.setState({ isStyleOfBusinessChangeModal: !this.state.isStyleOfBusinessChangeModal })}
                    >
                        {styleOfBusinessTaxExemptionHeading}
                    </ModalHeader>
                )}
                <ModalBody>
                    <RichTextComponent text={styleOfBusinessTaxExemptionMessage} />
                </ModalBody>
                <ModalFooter style={{ justifyContent: 'flex-end' }}>
                    <SignUpButton
                        key='style-continue'
                        id='style-continue'
                        className={`${this.moduleClassName}__create-button`}
                        ariaLabel={'style-continue'}
                        text={'OK'}
                        disabled={false}
                        onClick={() => {
                            this.setState({ isStyleOfBusinessChangeModal: !this.state.isStyleOfBusinessChangeModal });
                            this._submitProspectCall();
                        }}
                        telemetryContent={this.telemetryContent}
                    />
                </ModalFooter>
            </Modal>
        );
    };

    private _onValidationSuccess = (): React.ReactNode => {
        const {
            resources: { orgName, email },
            config: { accountFoundAgainst }
        } = this.props;
        const modalPropsTerms: IModalProps = {
            applicationNode: MsDyn365.isBrowser ? document.body : '',
            isOpen: this.state.existingUserSyscoValidationTrueModal,
            autoFocus: true,
            size: 'sm',
            keyboard: true,
            backdrop: true,
            zIndex: 1000,
            fade: true
        };
        const companyEmail = this._getItem(InputID.Email);

        return (
            <Modal {...modalPropsTerms}>
                {!this.state.loadDocuSign && (
                    <ModalHeader
                        closeAriaLabel='Close'
                        closeButtonLabel='Close'
                        toggle={() => this.setState({ existingUserSyscoValidationTrueModal: false })}
                    ></ModalHeader>
                )}
                <ModalBody>
                    <div className='account-success'>
                        <RichTextComponent text={accountFoundAgainst} />
                        <div className='text'>
                            <div className='heading'>{orgName}:</div>{' '}
                            <div className='data'>{this.state.existingUserSyscoValidationResult?.OrganizationName}</div>
                        </div>
                        <div className='text'>
                            <div className='heading'>{email}:</div> <div className='data'>{companyEmail?.value}</div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter style={{ justifyContent: 'flex-end' }}>
                    <SignUpButton
                        key='validation-success-back'
                        id='validation-exit-back'
                        className={`${this.moduleClassName}__create-button`}
                        ariaLabel={'validation-exit-back'}
                        text={'Back'}
                        disabled={false}
                        onClick={() => this.setState({ existingUserSyscoValidationTrueModal: false })}
                        telemetryContent={this.telemetryContent}
                    />
                    <SignUpButton
                        key='validation-success-ok'
                        id='validation-success-ok'
                        className={`${this.moduleClassName}__create-button`}
                        ariaLabel={'validation-success-ok'}
                        text={'Continue'}
                        disabled={this.state.isExistingUserSyscoreroutingLoading}
                        onClick={() => this._createUserAccount()}
                        telemetryContent={this.telemetryContent}
                    />
                </ModalFooter>
            </Modal>
        );
    };

    private readonly _createUserAccount = async () => {
        this.setState({ isExistingUserSyscoreroutingLoading: true });
        const { config } = this.props;
        const account = this._getItem(InputID.AccountNo);
        const companyEmail = this._getItem(InputID.Email);
        const firstName = this._getItem(InputID.FirstName);
        const lastName = this._getItem(InputID.LastName);
        const phone = this._getItem(InputID.Phone);

        if (account?.value && companyEmail?.value && firstName?.value && lastName?.value && phone?.value) {
            createExistingCustomerUserAsync(
                { callerContext: this.props.context.actionContext },
                `067-${account.value}`,
                firstName.value,
                lastName.value,
                companyEmail.value,
                this.state.phoneMarketing,
                this.state.emailMarketing,
                phone.value.replace(/[-()\s]/g, '')
            )
                .then(val => {
                    if (val?.Success) {
                        const successPageURL =
                            (config.confirmationLink &&
                                config.confirmationLink.linkUrl &&
                                config.confirmationLink.linkUrl.destinationUrl) ||
                            getUrlSync('home', this.props.context.actionContext) ||
                            '';
                        window.location.href = successPageURL;
                        this.setState({ isExistingUserSyscoreroutingLoading: false });
                    }
                })
                .catch(() => {
                    this.setState({ isExistingUserSyscoreroutingLoading: false });
                });
        }
    };

    private getValidationErrorMessage(
        accountNotFound: RichText | undefined,
        emailAlreadyExists: string,
        credentialsError: RichText | undefined
    ): string | React.ReactNode {
        const { existingUserSyscoValidationResult } = this.state;
        const account = this._getItem(InputID.AccountNo);
        const companyEmail = this._getItem(InputID.Email);

        if (!existingUserSyscoValidationResult?.AccountValidation) {
            if (!existingUserSyscoValidationResult?.IsAccountFound) {
                return <RichTextComponent text={accountNotFound} />;
            } else {
                return <RichTextComponent text={credentialsError} />;
            }
        } else if (!existingUserSyscoValidationResult?.IsAccountFound) {
            return (
                <div className={'errorMessageStyle'}>
                    {accountNotFound}
                    {account?.value}
                </div>
            );
        } else if (existingUserSyscoValidationResult?.EmailAlreadyExists) {
            return (
                <div className={'errorMessageStyle'}>
                    {emailAlreadyExists} {companyEmail?.value}
                </div>
            );
        }
        return <></>;
    }

    private _onValidationError = (): React.ReactNode => {
        const {
            resources: { emailAlreadyExists },
            config: { credentialsError, accountNotFoundAgainst }
        } = this.props;
        const modalPropsTerms: IModalProps = {
            applicationNode: MsDyn365.isBrowser ? document.body : '',
            isOpen: this.state.existingUserSyscoValidationFalseModal,
            autoFocus: true,
            size: 'sm',
            keyboard: true,
            backdrop: true,
            zIndex: 1000,
            fade: true
        };

        return (
            <Modal {...modalPropsTerms}>
                {!this.state.loadDocuSign && (
                    <ModalHeader
                        closeAriaLabel='Close'
                        closeButtonLabel='Close'
                        toggle={() => this.setState({ existingUserSyscoValidationFalseModal: false })}
                    ></ModalHeader>
                )}
                <ModalBody>
                    <>{this.getValidationErrorMessage(accountNotFoundAgainst, emailAlreadyExists, credentialsError)}</>
                </ModalBody>
                <ModalFooter style={{ justifyContent: 'flex-end' }}>
                    <SignUpButton
                        key='validation-exit-ok'
                        id='validation-exit-ok'
                        className={`${this.moduleClassName}__create-button`}
                        ariaLabel={'validation-exit-ok'}
                        text={'Ok'}
                        disabled={false}
                        onClick={() => this.setState({ existingUserSyscoValidationFalseModal: false })}
                        telemetryContent={this.telemetryContent}
                    />
                </ModalFooter>
            </Modal>
        );
    };

    private readonly _onSubmit = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (this.state.existingCustomerValue === 'yes') {
            this.setState({ isExistingUserSyscoValidationResultLoading: true });
            const account = this._getItem(InputID.AccountNo);
            const companyEmail = this._getItem(InputID.Email);
            const streetAddress = this._getItem(InputID.StreetAddress);
            const zipCode = this._getItem(InputID.ZipCode);

            if (account?.value && companyEmail?.value) {
                try {
                    const validatedData = await validateOrganizationAsync(
                        { callerContext: this.props.context.actionContext },
                        `067-${account.value}`,
                        companyEmail.value,
                        zipCode?.value?.trim() ?? '',
                        streetAddress?.value?.trim() ?? ''
                    );
                    if (validatedData) {
                        this.setState({
                            existingUserSyscoValidationResult: {
                                EmailAlreadyExists: validatedData.EmailAlreadyExists,
                                IsAccountFound: validatedData.IsAccountFound,
                                OrganizationName: validatedData?.OrganizationName ?? '',
                                AccountValidation: validatedData?.AccountValidation,
                                ValidationResult: validatedData.ValidationResult
                            },
                            existingUserSyscoValidationFalseModal: !validatedData.ValidationResult,
                            existingUserSyscoValidationTrueModal: validatedData.ValidationResult
                        });
                    }
                    this.setState({ isExistingUserSyscoValidationResultLoading: false });
                } catch (error) {
                    this.setState({ isExistingUserSyscoValidationResultLoading: false });
                    console.error('Error occurred on SubmitProspect Call:', error);
                }
            }
        } else {
            this.setState({ isLoading: true });

            try {
                const {
                    context: { actionContext }
                } = this.props;
                const selectedStyleOfBusiness = this.state.styleOfBusinesses.find(
                    style => style.RECID.toString() === this.state.styleofbusiness
                );
                const selectedStore = this.state.allStores.find(singleStore => singleStore.OrgUnitNumber === this.state.storeName);
                const customTaxExempted: ITaxExemptionCheckResponse = await taxExemptionCheckAsync(
                    { callerContext: actionContext },

                    selectedStore?.State ?? '',
                    selectedStyleOfBusiness?.StyleOfBusinessName ?? ''
                );

                if (customTaxExempted?.BypassTaxExemption) {
                    this._submitProspectCall();
                    return;
                }
                // if (customTaxExempted && customTaxExempted.ApplicableForTaxExemption && customTaxExempted.DocuSignWebFormFound) {
                this.setState(
                    prevState => ({ moduleUpdating: true }),
                    () => {
                        this.setState({
                            isTaxEempted: customTaxExempted.ApplicableForTaxExemption ?? false
                        });
                    }
                );
                // }
            } catch (error) {
                console.error('Error occurred on SubmitProspect Call:', error);
            }

            if (this.state.addressHasErrors) {
                this.setState({
                    showAddressErrors: true
                });

                event.preventDefault();
                return;
            }
            if (!this.state.isResale) {
                this._submitProspectCall();
            } else {
                this.setState({ isModalOpen: true, isLoading: false, isStyleOfBusinessChangeModal: false });
            }
        }
    };
    private loadDocuSign = async () => {
        const { docuSignInstanceId, docuSignWebFormURL, loadDocuSign, docuSignInstanceToken, integrationKey } = this.state;

        if (!docuSignInstanceId || !docuSignInstanceToken || !docuSignWebFormURL || !loadDocuSign) {
            return;
        }

        // Early exit if not in browser environment
        // @ts-ignore
        if (!MsDyn365.isBrowser) {
            console.error('Not in browser environment');
            return;
        }

        // Safely check for DocuSign SDK with retry mechanism
        await this.ensureDocuSignSDKLoaded();

        try {
            // @ts-ignore
            const { loadDocuSign: loadDS } = window.DocuSign;
            const docusign = await loadDS(integrationKey);

            // const webFormOptions = {
            //     instanceToken: docuSignInstanceToken,
            //     hideProgressBar: false,
            //     styles: { fontColor: 'black' },
            //     iframeStyles: { minHeight: '800px' },
            //     autoResizeHeight: true,
            //     showFinishLater: false
            // };
            const webFormOptions = {
                instanceToken: docuSignInstanceToken,
                hideProgressBar: false,
                styles: {
                    fontColor: 'black',
                    fontSize: '16px',
                    lineHeight: '1.5'
                },
                iframeStyles: {
                    // minHeight: '1150px',
                    // height: '1150px',
                    width: '100%',
                    border: 'none'
                    // '@media (max-width: 768px)': {
                    //     minHeight: '1020px',
                    //     height: '1020px'
                    // }
                },
                autoResizeHeight: true,
                showFinishLater: false,
                responsive: true
            };

            const webFormWidget = docusign.webforms({
                url: docuSignWebFormURL,
                options: webFormOptions
            });

            // Setup event listeners
            this.setupEventListeners(webFormWidget);

            // Mount with retry and visibility check
            await this.mountDocuSignWithRetry(webFormWidget);
        } catch (error) {
            console.error('DocuSign initialization failed:', error);
            // Consider adding retry logic here if needed
        }
    };

    private ensureDocuSignSDKLoaded = async (retries = 3, delay = 500): Promise<void> => {
        // @ts-ignore
        if (window.DocuSign) {
            return;
        }

        if (retries <= 0) {
            console.error('DocuSign SDK not loaded after multiple attempts');
            throw new Error('DocuSign SDK not loaded');
        }

        await new Promise(resolve => setTimeout(resolve, delay));
        return this.ensureDocuSignSDKLoaded(retries - 1, delay * 1.5);
    };

    private setupEventListeners = (webFormWidget: any) => {
        // Single message event handler
        const messageHandler = async (event: MessageEvent) => {
            // Skip if not from DocuSign
            if (!event.origin.includes('docusign.com')) {
                return;
            }

            try {
                // @ts-ignore
                const { eventType, content } = event.data;

                if (eventType === 'sessionEnd' && content) {
                    // @ts-ignore
                    switch (content.sessionEndType) {
                        case 'signing_complete':
                            this.handleSigningComplete();
                            break;
                        case 'finishLater':
                            this.handleFinishLater();
                            break;
                        case '':
                            await this.handleSessionRefresh();
                            break;
                    }
                }
            } catch (error) {
                console.error('Error handling DocuSign message:', error);
            }
        };

        window.addEventListener('message', messageHandler);

        // WebForm widget events
        webFormWidget.on('sessionEnd', async (event: any) => {
            try {
                switch (event.type) {
                    case 'sessionTimeout':
                        await this.handleSessionRefresh();
                        break;
                    case 'signingResult':
                        this.handleSigningComplete();
                        break;
                    case 'finishLater':
                        this.handleFinishLater();
                        break;
                }
            } catch (error) {
                console.error('Error handling sessionEnd event:', error);
            }
        });
    };

    private handleSigningComplete = () => {
        this.setState({ SignUpVia: 'docusign', isModalOpen: false }, () => this._submitProspectCall());
    };

    private handleFinishLater = () => {
        this.setState({ SignUpVia: 'docuSignInComplete' }, () => this._submitProspectCall());
    };

    private handleSessionRefresh = async () => {
        try {
            const response: IRefreshWebFormInstanceTokenResponse = await refreshWebFormInstanceTokenAsync(
                // @ts-ignore
                { callerContext: this.props.context.actionContext },
                this.state.docuSignWebFormURL,
                this.state.docuSignInstanceId
            );

            if (response?.FormInstanceToken && response.InstanceID && response.FormURL && response.IntegrationKey) {
                await this.setStateAsync({
                    docuSignWebFormURL: response.FormURL,
                    docuSignInstanceId: response.InstanceID,
                    loadDocuSign: true,
                    docuSignInstanceToken: response.FormInstanceToken,
                    integrationKey: response.IntegrationKey,
                    isLoading: false
                });

                // Retry loading DocuSign
                await this.loadDocuSign();
            }
        } catch (error) {
            console.error('Error refreshing session:', error);
        }
    };

    private setStateAsync = (state: any): Promise<void> => {
        return new Promise(resolve => this.setState(state, resolve));
    };

    private mountDocuSignWithRetry = async (webFormWidget: any, retries = 3, delay = 300): Promise<void> => {
        if (retries <= 0) {
            console.error('Failed to mount DocuSign after multiple attempts');
            return;
        }

        if (!this.docusignContainerRef.current) {
            await new Promise(resolve => setTimeout(resolve, delay));
            return this.mountDocuSignWithRetry(webFormWidget, retries - 1, delay * 1.5);
        }

        try {
            // webFormWidget.mount(this.docusignContainerRef.current);
            setTimeout(() => {
                if (this.docusignContainerRef.current) {
                    webFormWidget.mount(this.docusignContainerRef.current);
                }
            }, 100);
        } catch (error) {
            console.error('Mounting failed, retrying...', error);
            await new Promise(resolve => setTimeout(resolve, delay));
            return this.mountDocuSignWithRetry(webFormWidget, retries - 1, delay * 1.5);
        }
    };
    private fetchDocuSignData = async (simpleUpload: boolean) => {
        const {
            config: { sellerName }
        } = this.props;
        this.setState({ isLoading: true });
        const companyAddress = this._getItem(InputID.Address, true);
        const firstName = this._getItem(InputID.FirstName);
        const lastName = this._getItem(InputID.LastName);
        const phoneNumber =
            companyAddress && companyAddress.address && companyAddress.address.Phone
                ? companyAddress.address.Phone.replace(/[-() ]/g, '')
                : '';
        try {
            const selectedStore = this.state.allStores.find(singleStore => singleStore.OrgUnitNumber === this.state.storeName);
            const selectedStyleOfBusiness = this.state.styleOfBusinesses.find(
                style => style.RECID.toString() === this.state.styleofbusiness
            );
            const sellerAddress = this.state.formItems.find(item => item.id === 'CompanyAddress');
            const companyName = this.state.formItems.find(item => item.id === 'CompanyName');
            const preFilledFields: ITaxFormPreFilledFieldsEntity = {
                Email: this._getItem(InputID.Email)?.value ?? '',
                TaxId: this.state.taxId,
                StyleOfBusiness: selectedStyleOfBusiness?.StyleOfBusinessName,
                StatePurchaser: sellerAddress?.address?.State,
                CityPurchaser: sellerAddress?.address?.City,
                AddressPurchaser: sellerAddress?.address?.Street,
                ZipCodePurchaser: sellerAddress?.address?.ZipCode,
                FullName: `${firstName?.value} ${lastName?.value}`,
                AddressSeller: selectedStore?.Street,
                CitySeller: selectedStore?.City,
                StateSeller: selectedStore?.State,
                ZipCodeSeller: selectedStore?.Zip,
                PhoneNumber: phoneNumber,
                NameOfPurchaser: companyName?.value,
                Title: '',
                Seller: sellerName ?? selectedStore?.OrgUnitName
            };

            let response: ICreateWebFormInstanceTokenResponse | null = null;

            if (simpleUpload) {
                response = await createDefaultFormInstanceTokenAsync({ callerContext: this.props.context.actionContext }, preFilledFields);
            } else {
                response = await createWebFormInstanceTokenAsync({ callerContext: this.props.context.actionContext }, preFilledFields);
            }
            if (
                response &&
                response.FormInstanceToken &&
                response.InstanceID &&
                response.FormURL &&
                response.IntegrationKey &&
                response.WebFormID
            ) {
                this.setState({
                    docuSignWebFormURL: response.FormURL,
                    docuSignInstanceId: response.InstanceID,
                    loadDocuSign: true,
                    docuSignInstanceToken: response.FormInstanceToken,
                    SignUpVia: 'docuSignInComplete',
                    integrationKey: response.IntegrationKey,
                    docuSignWebFormId: response.WebFormID
                });
                await this.loadDocuSign();
            }
        } catch (error) {
            console.error('Error fetching DocuSign data:', error);
        }
    };

    private _renderModal = (): React.ReactNode => {
        const {
            resources: { modalLabel2, docuSignButtonLabel, notReadyText, exitLinkText, finishItLater, docuUploadButtonLabel, modalLabel1 }
        } = this.props;
        const modalPropsTerms: IModalProps = {
            applicationNode: MsDyn365.isBrowser ? document.body : '',
            isOpen: this.state.isModalOpen,
            autoFocus: true,
            size: 'sm',
            keyboard: true,
            backdrop: true,
            zIndex: 1000,
            fade: true,
            className: `${this.state.loadDocuSign ? 'document-sign' : ''}`
        };
        // const docuSignUrl = `https://webforms.docusign.com/signing/${this.state.docuSignWebFormId}?instance=${this.state.docuSignInstanceId}`;
        return (
            <Modal {...modalPropsTerms}>
                {!this.state.loadDocuSign && (
                    <ModalHeader
                        closeAriaLabel='Close'
                        closeButtonLabel='Close'
                        toggle={() => this.setState({ isModalOpen: !this.state.isModalOpen })}
                    ></ModalHeader>
                )}
                <ModalBody>
                    {!this.state.loadDocuSign && !this.state.isStyleOfBusinessChangeModal && (
                        <div className='btnPosition'>
                            {this.state.isTaxEempted && (
                                <div className='wrapper'>
                                    <div className='docuSignModalHeader'>
                                        {this.state.isTaxEempted && (
                                            <p>
                                                {modalLabel2}
                                                <br />
                                            </p>
                                        )}
                                    </div>

                                    <SignUpButton
                                        key='submit-doc-sign'
                                        id='submit-doc-sign'
                                        className={`${this.moduleClassName}__create-button`}
                                        ariaLabel={'submit-doc-sign'}
                                        text={docuSignButtonLabel}
                                        onClick={() => this.fetchDocuSignData(false)}
                                        telemetryContent={this.telemetryContent}
                                    />

                                    <hr className='verticalDivider' />
                                </div>
                            )}
                            <div className='wrapper'>
                                <div className='docuSignModalHeader'>
                                    <p>
                                        {modalLabel1}
                                        <br />
                                    </p>
                                </div>
                                <SignUpButton
                                    key='submit-doc-upload'
                                    id='submit-doc-upload'
                                    className={`${this.moduleClassName}__create-button`}
                                    ariaLabel={'submit-doc-upload'}
                                    text={docuUploadButtonLabel}
                                    onClick={() => this.fetchDocuSignData(true)}
                                    telemetryContent={this.telemetryContent}
                                />
                            </div>
                        </div>
                    )}
                    {this.state.loadDocuSign && <div ref={this.docusignContainerRef} className='webform-iframe-container'></div>}
                    {/* </div> */}
                </ModalBody>
                <ModalFooter style={{ justifyContent: 'center', fontSize: '14px' }}>
                    {!this.state.loadDocuSign && (
                        <span className='ms-checkout-terms-and-conditions__text'>
                            {notReadyText}&nbsp;&nbsp;
                            <a
                                href='javascript:void(0)'
                                onClick={() => {
                                    this.setState({ isExitSystemModal: true, isModalOpen: false, isStyleOfBusinessChangeModal: false });
                                }}
                                className='ms-checkout__terms-and-conditions__link'
                                style={{ color: ' #008CD2', textDecoration: 'underline' }}
                            >
                                {exitLinkText}
                            </a>
                        </span>
                    )}
                    {this.state.loadDocuSign && (
                        <span className='ms-checkout-terms-and-conditions__text'>
                            <a
                                href='javascript:void(0)'
                                onClick={this._submitProspectCall}
                                className='ms-checkout__terms-and-conditions__link'
                                style={{ color: ' #008CD2', textDecoration: 'underline' }}
                            >
                                {finishItLater}
                            </a>
                        </span>
                    )}
                </ModalFooter>
            </Modal>
        );
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        const cleanForm = this.state.formItems;
        cleanForm.forEach(input => {
            input.value = '';
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            formItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            return item.id === id && item.value !== '';
        });
    };
}

export default BusinessSignUp;
